import clsx from 'clsx'
import PropTypes from 'prop-types'
import React, { useCallback, useState } from 'react'
import { Modal } from 'react-bootstrap'
import CloseConfirmationModal, { useCloseConfirmationModal } from '../../../../../components/CloseConfirmationModal'
import TemplateGroupCreate from './TemplateGroupCreate'
import TemplateGroupEdit from './TemplateGroupEdit'
import TemplateGroupView from './TemplateGroupView'

export const MODAL_STATES = {
  VIEW: 'VIEW',
  EDIT: 'EDIT',
  CREATE: 'CREATE',
}

const propTypes = {
  /**
   * If true, shows the modal
   */
  show: PropTypes.bool,
  /**
   * Callback called when modal is going to be hidden
   */
  onHide: PropTypes.func.isRequired,
  /**
   * Array with the templates
   */
  policyTemplates: PropTypes.array, // eslint-disable-line react/forbid-prop-types
  /**
   * map of form options that are non tenant specific
   */
  globalFormOptionsMap: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  /**
   * Indicates if policy template group api is being processed
   */
  loadingPolicyTemplateGroup: PropTypes.bool,
  /**
   * Indicates error in policy template group api operation
   */
  errorPolicyTemplateGroup: PropTypes.bool,
  /**
   * Create policy template group api
   */
  createPolicyTemplateGroup: PropTypes.func.isRequired,
  /**
   * Update policy template group api
   */
  updatePolicyTemplateGroup: PropTypes.func.isRequired,
  /**
   * Fetch policy template group api
   */
  fetchPolicyTemplateGroupList: PropTypes.func.isRequired,
  /**
   * modal state when opened
   */
  modalState: PropTypes.string,
  /**
   * Selected template group object
   */
  selectedTemplateGroup: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  /**
   * Callback to open the modal setting the modal state
   */
  openTemplateGroupModal: PropTypes.func.isRequired,
}

const defaultProps = {
  show: false,
  policyTemplates: [],
  globalFormOptionsMap: {},
  loadingPolicyTemplateGroup: false,
  errorPolicyTemplateGroup: null,
  modalState: MODAL_STATES.VIEW,
  selectedTemplateGroup: {},
}

function TemplateGroupModal(props) {
  const {
    show,
    onHide,
    policyTemplates,
    globalFormOptionsMap,
    loadingPolicyTemplateGroup,
    errorPolicyTemplateGroup,
    createPolicyTemplateGroup,
    updatePolicyTemplateGroup,
    fetchPolicyTemplateGroupList,
    modalState,
    selectedTemplateGroup,
    openTemplateGroupModal,
  } = props

  const [hasFormChanged, setHasFormChanged] = useState(false)

  const shouldShowCloseConfirmation = useCallback(
    () => hasFormChanged && (modalState === MODAL_STATES.CREATE || MODAL_STATES.EDIT),
    [hasFormChanged, modalState],
  )

  const { showCloseConfirmationModal, hideCloseConfirmationModal, onHideOverride } = useCloseConfirmationModal({
    onHide,
    shouldShowCloseConfirmation,
  })

  return (
    <>
      <Modal
        size="lg"
        backdrop
        className={clsx('right text-color-default', {
          in: show,
        })}
        show={show}
        onHide={onHideOverride}
      >
        {modalState === MODAL_STATES.CREATE && (
          <TemplateGroupCreate
            onHide={onHideOverride}
            onSuccess={onHide}
            policyTemplates={policyTemplates}
            globalFormOptionsMap={globalFormOptionsMap}
            loadingPolicyTemplateGroup={loadingPolicyTemplateGroup}
            errorPolicyTemplateGroup={errorPolicyTemplateGroup}
            createPolicyTemplateGroup={createPolicyTemplateGroup}
            fetchPolicyTemplateGroupList={fetchPolicyTemplateGroupList}
            setHasFormChanged={setHasFormChanged}
          />
        )}
        {modalState === MODAL_STATES.EDIT && (
          <TemplateGroupEdit
            onHide={onHideOverride}
            onSuccess={onHide}
            policyTemplates={policyTemplates}
            globalFormOptionsMap={globalFormOptionsMap}
            loadingPolicyTemplateGroup={loadingPolicyTemplateGroup}
            errorPolicyTemplateGroup={errorPolicyTemplateGroup}
            updatePolicyTemplateGroup={updatePolicyTemplateGroup}
            fetchPolicyTemplateGroupList={fetchPolicyTemplateGroupList}
            selectedTemplateGroup={selectedTemplateGroup}
            setHasFormChanged={setHasFormChanged}
          />
        )}
        {modalState === MODAL_STATES.VIEW && (
          <TemplateGroupView
            onHide={onHideOverride}
            globalFormOptionsMap={globalFormOptionsMap}
            selectedTemplateGroup={selectedTemplateGroup}
            onEditClick={() => openTemplateGroupModal(selectedTemplateGroup, MODAL_STATES.EDIT)}
          />
        )}
      </Modal>

      <CloseConfirmationModal
        show={showCloseConfirmationModal}
        onHideConfirmation={hideCloseConfirmationModal}
        onHideParentModal={onHide}
      />
    </>
  )
}

TemplateGroupModal.propTypes = propTypes
TemplateGroupModal.defaultProps = defaultProps

export default TemplateGroupModal
